/* eslint-disable no-restricted-syntax */
import type { ChangeEvent } from 'react';
import React, { useRef } from 'react';

import { ALLOWED_FILE_TYPES } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

type ClassListAdd = { target: { classList: { add: (className: string) => void } } };

type ClassListRemove = { target: { classList: { remove: (className: string) => void } } };

type TargetFiles = { target: { files: File[] } };

type Props = {
    accept?: Array<string> | null;
    children: React.ReactNode;
    className?: string;
    size?: number;
    disabled?: boolean;
    register?: {
        ref: (element: HTMLInputElement) => void;
        onChange: (e: (ChangeEvent & TargetFiles) | { target: HTMLInputElement }) => void;
    };
    multiple?: boolean;
    onSelect: (files: File[] | File | null) => void;
    /** When set, validation failures are reported here instead of via a toast. */
    onError?: (message: string) => void;
};

export const FilePicker: React.FC<Props> = ({
    accept,
    children,
    className,
    disabled,
    multiple,
    onError,
    onSelect,
    register,
    size,
}) => {
    const { t } = useTranslation();
    const inputField = useRef<(HTMLInputElement & TargetFiles) | undefined>(undefined);

    const reportError = (message: string) => {
        if (onError) onError(message);
        else toast.error(message);
    };

    const handleFiles = (e: (ChangeEvent & TargetFiles) | TargetFiles) => {
        if (e.target.files.length === 0) return;

        for (const file of e.target.files) {
            const allowedTypes = accept || ALLOWED_FILE_TYPES;
            if (!allowedTypes.includes(file.type as (typeof ALLOWED_FILE_TYPES)[number])) {
                if (inputField.current && 'value' in inputField.current) inputField.current.value = '';
                reportError(t('FILE_UPLOAD.INVALID_FILE_TYPE'));
                onSelect(null);
                return;
            }
            if (size) {
                if (file.size > size) {
                    if (inputField.current && 'value' in inputField.current) inputField.current.value = '';
                    reportError(t('FILE_UPLOAD.FILE_TOO_LARGE', { count: size / 1024 / 1024 }));
                    onSelect(null);
                    return;
                }
            }
        }

        if (multiple) onSelect([...e.target.files] /* transform to array */);
        else onSelect(e.target.files[0]);
        // Clear the input so picking the same file again still fires change
        if (!register && inputField.current && 'value' in inputField.current) inputField.current.value = '';
    };

    const setRef = (element: HTMLInputElement & TargetFiles) => {
        if (register) register.ref(element);
        inputField.current = element;
    };

    const onChange = (e: ChangeEvent<HTMLInputElement> & TargetFiles) => {
        if (e.target) if (register) register.onChange(e);
        handleFiles(e);
    };

    const onDragStart = (e: React.DragEvent<HTMLDivElement> & ClassListAdd) => {
        e.preventDefault(); // prevent opening the file inside browser tab
        if (e.target) e.target.classList.add('dragover');
    };

    const onDragEnd = (e: React.DragEvent<HTMLDivElement> & ClassListRemove) => {
        e.target.classList.remove('dragover');
    };

    const onFilesDropped = (e: React.DragEvent<HTMLDivElement> & ClassListRemove) => {
        e.stopPropagation();
        e.preventDefault();
        onDragEnd(e);

        const { files } = e.dataTransfer;
        if (files.length && inputField.current) {
            inputField.current.files = files;

            // workaround because setting fileinput.files does not trigger a change event for fileinput
            // inputField.current.dispatchEvent(new Event('change')); does not work with react
            // so we call the handler directly with a fake change event and just set the stuff that the handler needs
            if (register)
                register.onChange({
                    target: inputField.current,
                });

            handleFiles({
                target: { files: [...files] },
            });
        }
    };
    return (
        <div
            className={className}
            onClick={() => inputField.current?.click()}
            onDragOver={onDragStart}
            onDragLeave={onDragEnd}
            onDragEnd={onDragEnd}
            onDrop={onFilesDropped}
        >
            <span className="flex items-center justify-center space-x-3">{children}</span>
            <input
                data-testid="upload-file-input"
                type="file"
                className="hidden"
                accept={accept ? accept?.join(',') : ALLOWED_FILE_TYPES.join(',')}
                multiple={multiple}
                disabled={disabled}
                {...register}
                ref={setRef}
                onChange={onChange}
            />
        </div>
    );
};
